import { CoreIntegrationTypes } from '../types/core-integration-types';
import { BaseOrganizationEndpoints } from './base';

export const CORE_INTEGRATIONS_BASE_PATH = '/v1/integrations';

type CoreIntegrationItem = {
  id: string;
  type: CoreIntegrationTypes;
  created: string; //"2024-12-10T02:37:24.22643Z"
  updated: string; //"2024-12-10T02:37:24.22643Z"
};

type ResGetCoreIntegrations = {
  integrations: CoreIntegrationItem[];
};

export class CoreIntegrations extends BaseOrganizationEndpoints {
  async create<P, R>(type: CoreIntegrationTypes, payload: P) {
    return this.requestWithStatus<R>(
      'POST',
      `${CORE_INTEGRATIONS_BASE_PATH}/${type}`,
      payload
    );
  }

  async delete(id: string) {
    return this.requestWithStatus(
      'DELETE',
      `${CORE_INTEGRATIONS_BASE_PATH}/${id}`
    );
  }

  async fetch<T>(id: string) {
    return this.requestWithStatus<T>(
      'GET',
      `${CORE_INTEGRATIONS_BASE_PATH}/${id}`
    );
  }

  async update<P, R>(id: string, payload: P) {
    return this.requestWithStatus<R>(
      'PUT',
      `${CORE_INTEGRATIONS_BASE_PATH}/${id}`,
      payload
    );
  }

  async all() {
    return this.requestWithStatus<ResGetCoreIntegrations>(
      'GET',
      CORE_INTEGRATIONS_BASE_PATH
    );
  }

  async isInstalled<T>(
    type: CoreIntegrationTypes
  ): Promise<IsInstalledType<T | null>> {
    try {
      const allResponse = await this.all();
      const { ok, result } = allResponse;
      if (!ok || !result) {
        return createErrorResult(
          'Error fetching all integrations',
          allResponse
        );
      }
      const integration = result.integrations.find((i) => i.type === type);
      if (!integration) {
        return createNotInstalledResult(type);
      }
      try {
        const fullIntegrationResponse = await this.fetch<T>(integration.id);
        if (!fullIntegrationResponse.ok) {
          return createErrorResult(
            'Error fetching full integration details',
            fullIntegrationResponse
          );
        }
        return {
          ok: true,
          isInstalled: true,
          fullIntegration: fullIntegrationResponse.result,
          id: integration.id
        };
      } catch (fetchError) {
        return createErrorResult(
          'Error fetching full integration details',
          fetchError
        );
      }
    } catch (allError) {
      return createErrorResult('Error fetching all integrations', allError);
    }
  }
}

type IsInstalledType<T> = {
  ok: boolean;
  isInstalled: boolean;
  fullIntegration: T | null;
  id: null | string;
};

function createNotInstalledResult(
  type: CoreIntegrationTypes
): IsInstalledType<null> {
  console.warn(`Integration not found: ${type}`);
  return {
    ok: true,
    isInstalled: false,
    fullIntegration: null,
    id: null
  };
}

function createErrorResult(
  message: string,
  error?: any
): IsInstalledType<null> {
  console.error(message, error);
  return {
    ok: false,
    isInstalled: false,
    fullIntegration: null,
    id: null
  };
}
